import React, { SyntheticEvent, useEffect, useMemo, useState } from "react";
import { Autocomplete, AutocompletePropsSizeOverrides, TextField } from "@mui/material";
import { OverridableStringUnion } from "@mui/types";

export interface LanguageOption {
  code: string,
  language: string,
  locale: string,
  region: string
}

interface LanguageAutocompleteProps {
  /**
   * The currently selected locale (e.g. "en-US").  This property must be externally managed. 
   * @default navigator.language
   */
  locale?: string;
  /**
   * Callback fired when the language is changed.
   */
  onChange(locale: LanguageOption): void;
  /**
   * Callback fired when the default language is set.
   */
  onDefault?(locale: string): void;
  /**
   * The size of the component.
   * @default 'medium'
   */
  size?: OverridableStringUnion<'small' | 'medium', AutocompletePropsSizeOverrides>;
}

export default function(props: LanguageAutocompleteProps): React.JSX.Element {
  const locale = props.locale || navigator.language;
  const localeParts = locale.split("-");
  const languageRegion = localeParts.length > 1 ? localeParts[1] : navigator.language.split("-")[1];
  let languageCode = localeParts[0];
  // TODO: We need to find a better way to determine supported languages.
  const supportedLanguageCodes = ["en", "fr", "es", "pt", "nl"];
  if (!supportedLanguageCodes.includes(languageCode)) {
    languageCode = "en";
  }

  const [selectedLanguageCode, setSelectedLanguageCode] = useState(languageCode);
  const languages = useMemo(buildLanguages, [languageCode]);
  const selectedLanguage = useMemo(getSelectedLanguage, [languages, selectedLanguageCode]);

  function buildLanguages(): Array<LanguageOption> {
    const localeLanguageName = new Intl.DisplayNames(languageCode, {type: "language"});
    const languageOptions: LanguageOption[] = supportedLanguageCodes.map((code) => {
      const nativeLanguageName = new Intl.DisplayNames(code, {type: "language"});

      const capitalize = s => (s && String(s[0]).toUpperCase() + String(s).slice(1)) || "";

      const nativeName = capitalize(nativeLanguageName.of(code));
      const localeName = capitalize(localeLanguageName.of(code));

      const language = nativeName !== localeName ? `${nativeName} / ${localeName}` : `${localeName}`;
      return {
        code,
        language,
        locale: `${code}-${languageRegion}`,
        region: languageRegion
      };
    });

    return languageOptions;
  }

  function getSelectedLanguage(): LanguageOption {
    const found = languages.find((option) => option.code === selectedLanguageCode);
    return found || languages[0];
  }

  function handleLanguageChange(event: SyntheticEvent, languageOption: LanguageOption): void {
    setSelectedLanguageCode(languageOption.code);
    props.onChange(languageOption);
  }

  useEffect(() => {
    props.onDefault && props.onDefault(locale);
  }, []);

  return (
    <Autocomplete 
      size={props.size}
      fullWidth={true}
      disableClearable
      getOptionLabel={(option) => option.language}
      options={languages}
      value={selectedLanguage}
      isOptionEqualToValue={(option: LanguageOption, value: LanguageOption) => option.locale === value.locale}
      onChange={handleLanguageChange}
      renderInput={(params) => <TextField autoComplete="off" {...params} label="" />} />
  )
}